import { defineStore } from "pinia";
import type {
  Role,
  WebModelCountryAndHubForUser,
  WebModelCreateUser,
  WebModelGetUser,
  WebModelUserLogin,
  WebModelConfirmUser,
  WebModelUpdateUserCountry,
  WebModelUpdateUserEmail,
  WebModelUpdateUser,
  WebModelUpdateUserPassword,
  WebModelPasswordResetConfirm,
  WebModelMfaActivationRequest,
  WebModelMfaActivationResponse,
  WebModelMfaConfirmResponse,
  WebModelMfaDeactivationRequest,
  WebModelMfaDeactivationResponse,
  WebModelMfaResetByRecoveryCodeResponse,
  WebModelMfaDeviceTokenInfoResponse,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  createGuestJwtAuthToken,
  deleteJwtAuthToken,
  findHubForUser,
  findUser,
  findUserRoles,
  getUserStatus,
  login,
  registerUser,
  confirmRegister,
  updateUserCountry,
  updateUserEmail,
  updateUser,
  updateUserPassword,
  triggerPasswordReset,
  confirmPasswordReset,
  createDeveloperRequest,
  cancelDeveloperRequest,
  activateMfa,
  confirmMfa,
  deactivateMfa,
  resetMfaByRecoveryCode,
  getMfaDeviceTokenInfo,
  getAllUserCompanyInvites,
  issueMfaRecoveryCode,
  getMfaRecoveryCodeInfo,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { withLoading } from "~/helpers/withLoading";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { useUiStore } from "~/stores/ui/uistore";
import { withErrorHandling } from "~/helpers/withErrorHandling";

interface State {
  auth: boolean;
  authUserUuid: string | null;
  authUser: WebModelGetUser | null;
  authUserRoles: string[];
  userRegistrationStatus: string[];
  becomeDeveloperAfterLogin: boolean;
  userHub: WebModelCountryAndHubForUser | null;
  mfaDeviceTokenCookieInfo: WebModelMfaDeviceTokenInfoResponse | null;
}

export const useUserStore = defineStore("user", {
  // convert to a function
  state: (): State => ({
    auth: false,
    authUserUuid: localStorage.getItem("userIsAuth"),
    authUser: null,
    authUserRoles: [],
    userRegistrationStatus: [],
    becomeDeveloperAfterLogin: false,
    userHub: null,
    mfaDeviceTokenCookieInfo: null,
  }),
  getters: {
    isLoggedIn: (state) => state.auth,
    isRegistrationComplete: (state): boolean => {
      return (
        !state.userRegistrationStatus.includes("USER_DATA") &&
        !state.userRegistrationStatus.includes("COMPANY_DATA")
      );
    },
    isUserDataComplete: (state): boolean => {
      return !state.userRegistrationStatus.includes("USER_DATA");
    },
    isCompanyDataComplete: (state): boolean => {
      return !state.userRegistrationStatus.includes("COMPANY_DATA");
    },
    isCompanyAdmin: (state): boolean => {
      return state.authUserRoles.includes("ROLE_COMPANY_ADMIN");
    },
    isStoreAdmin: (state): boolean => {
      return state.authUserRoles.includes("ROLE_ADMIN");
    },
    userWantsToBecomeDeveloper: (state): boolean => {
      return state.userRegistrationStatus.includes("DEVELOPER_REQUEST");
    },
    userHasCompanyInvite: (state): boolean => {
      return state.userRegistrationStatus.includes("COMPANY_INVITATION");
    },
    userMfaRequired: (state): boolean => {
      return state.userRegistrationStatus.includes("USER_MFA_REQUIRED");
    },
  },
  actions: {
    setAuth(uuid: string) {
      this.authUserUuid = uuid;
      this.auth = true;
      localStorage.setItem("userIsAuth", uuid);
    },
    clearAuth() {
      this.authUserUuid = null;
      this.auth = false;
      localStorage.removeItem("userIsAuth");
      sessionStorage.removeItem("user_mfa_required");
    },
    async logUserIn(params: WebModelUserLogin) {
      try {
        await withLoading(login(params));
        return { success: true, err: undefined };
      } catch (e) {
        return { success: false, err: e };
      }
    },

    async loadAuthUser() {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: loadAuthUser - No user UUID — skipping auth user load.",
        );
        return false;
      }
      try {
        const params = this.authUserUuid;
        const { data } = await findUser(params);
        this.authUser = data;
        console.debug("AUTH USER");
        console.debug(this.authUser);
        return true;
      } catch (e) {
        const errorMessage = useErrorMessage();
        errorMessage(e);
        return false;
      }
    },

    async loadAuthUserRoles() {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: loadAuthUserRoles - No user UUID — skipping auth user load.",
        );
        return;
      }
      try {
        const params = { "user-uuid": this.authUserUuid };
        const { data } = await findUserRoles(params);
        if (data) {
          this.authUserRoles = data.map((role: Role) => role.name);
        } else {
          this.authUserRoles = [];
        }
      } catch (e) {
        const errorMessage = useErrorMessage();
        errorMessage(e);
      }
    },

    async loadUserHub() {
      console.debug("loadUserHub");
      let params = {};
      if (this.authUserUuid) {
        params = { uuid: this.authUserUuid };
      }

      const { data } = await findHubForUser(params);
      console.info("loadUserHub: ", data);
      this.userHub = data;
    },

    async loadUserRegistrationStatus() {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: loadUserRegistrationStatus - No user UUID — skipping user status load.",
        );
        return;
      }
      // The endpoint requires a `params` object — provide userId or similar.
      const params = { uuid: this.authUserUuid };
      const { data } = await getUserStatus(params);
      this.userRegistrationStatus = data;
    },

    async loadMfaDeviceTokenInfo() {
      const { data } = await getMfaDeviceTokenInfo();
      this.mfaDeviceTokenCookieInfo = data;
    },

    async loadUserCompanyInvites() {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: loadUserRegistrationStatus - No user UUID — skipping user status load.",
        );
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(getAllUserCompanyInvites(this.authUserUuid)),
        ),
      );

      console.debug("userstore: loadUserCompanyInvites: ", data, error);

      if (error) {
        console.error("Error loading user company invites: ", error);
        return false;
      }
      return data;
    },

    async logout() {
      const authUserUuid = this.authUserUuid;
      if (authUserUuid) {
        await this.clearCookieAndLocalStorageAndState();
        sessionStorage.removeItem("user_mfa_required");
        try {
          await deleteJwtAuthToken({ data: { delete: true } });
          await createGuestJwtAuthToken();
          const router = useRouter();
          useUiStore().isFormDirty = false; // never prevent navigation on logout. its too late here anyway.
          await router.push("/");
        } catch (err) {
          console.error("Logout failed: ", err);
        }
      }
    },

    async registerUser(webModelCreateUser: WebModelCreateUser) {
      const authUserUuid = this.authUserUuid;
      if (authUserUuid) {
        console.warn("User is already registered. Skipping registration.");
        return;
      }
      await withErrorMessage(withLoading(registerUser(webModelCreateUser)));
    },

    async confirmUserRegistration(webModelConfirmUser: WebModelConfirmUser) {
      const authUserUuid = this.authUserUuid;
      if (authUserUuid) {
        console.warn("User is already registered. Skipping registration.");
        return;
      }
      await withErrorMessage(withLoading(confirmRegister(webModelConfirmUser)));
    },

    async selectRegion(webModelUpdateUserCountry: WebModelUpdateUserCountry) {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: loadUserRegistrationStatus - No user UUID — skipping user status load.",
        );
        return;
      }
      await withErrorMessage(
        withLoading(
          updateUserCountry(this.authUserUuid, webModelUpdateUserCountry),
        ),
      );
      await this.loadAuthUser(); // reload
    },

    async changeEmail(webModelUpdateUserCountry: WebModelUpdateUserEmail) {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: changeEmail - No user UUID — skipping change email",
        );
        return;
      }
      await withErrorMessage(
        withLoading(
          updateUserEmail(this.authUserUuid, webModelUpdateUserCountry),
        ),
      );
      await this.logout(); // logout to force re-login after new email is confirmed
    },

    async changePassword(
      webModelUpdateUserPassword: WebModelUpdateUserPassword,
    ) {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: changePassword - No user UUID — skipping change password",
        );
        return;
      }
      await withErrorMessage(
        withLoading(
          updateUserPassword(this.authUserUuid, webModelUpdateUserPassword),
        ),
      );
      await withErrorMessage(withLoading(this.loadUserRegistrationStatus()));
    },

    async activateMfa(
      webModelMfaActivationRequest: WebModelMfaActivationRequest,
    ): Promise<WebModelMfaActivationResponse> {
      if (!this.authUserUuid) {
        console.error(
          "userstore: activateMfa - No user UUID — skipping mfa activate",
        );
        //const t : WebModelMfaActivationResponse = {};
        return {};
      }

      const { data } = await withErrorMessage(
        withLoading(activateMfa(webModelMfaActivationRequest)),
      );

      return data;
    },

    async deactivateMfa(
      webModelMfaDeactivationRequest: WebModelMfaDeactivationRequest,
    ): Promise<WebModelMfaDeactivationResponse> {
      if (!this.authUserUuid) {
        console.error(
          "userstore: deactivateMfa - No user UUID — skipping mfa deactivate",
        );
        return {};
      }

      const { data } = await withErrorMessage(
        withLoading(deactivateMfa(webModelMfaDeactivationRequest)),
      );

      return data;
    },

    async confirmMfa(authCode: string): Promise<WebModelMfaConfirmResponse> {
      console.debug("userstore: confirmMfa" + authCode);
      if (!this.authUserUuid) {
        console.error(
          "userstore: confirmMfa - No user UUID — skipping mfa confirm",
        );
        return {};
      }
      const { data } = await withErrorMessage(
        withLoading(
          confirmMfa({ userUuid: this.authUserUuid, authCode: authCode }),
        ),
      );

      // reload user
      await this.loadAuthUser();
      return data;
    },

    async deactivateMfaByRecoveryCode(
      email: string,
      password: string,
      recoveryCode: string,
    ): Promise<WebModelMfaResetByRecoveryCodeResponse> {
      const { data } = await withErrorMessage(
        withLoading(
          resetMfaByRecoveryCode({
            email: email,
            password: password,
            recoveryCode: recoveryCode,
          }),
        ),
      );
      return data;
    },

    async confirmMfaReset(resetCode: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(withLoading(issueMfaRecoveryCode({ code: resetCode }))),
        ),
      );
      console.debug("confirmMfaReset", data, error);
      if (error) {
        return false;
      } else {
        return data;
      }
    },

    async getMfaRecoveryCodeInfo(code: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(getMfaRecoveryCodeInfo({ code: code })),
      );
      console.debug("getMfaRecoveryCodeInfo", data, error);
      return data;
    },

    async updateUserProfile(webModelUpdateUser: WebModelUpdateUser) {
      if (!this.authUserUuid) {
        console.debug("userstore: updateUserProfile - No user UUID — skipping");
        return;
      }
      const { data } = await withErrorMessage(
        withLoading(updateUser(this.authUserUuid, webModelUpdateUser)),
      );
      this.authUser = data; // update directly so we dont have to do another api call.
      await this.loadUserRegistrationStatus();
    },

    async resetPassword(email: string) {
      await withErrorMessage(withLoading(triggerPasswordReset({ email })));
    },

    async confirmPasswordReset(
      webModelPasswordResetConfirm: WebModelPasswordResetConfirm,
    ) {
      await withErrorMessage(
        withLoading(confirmPasswordReset(webModelPasswordResetConfirm)),
      );
    },

    async createDeveloperRequest() {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: createDeveloperRequest - No user UUID — skipping",
        );
        return;
      }
      await withErrorMessage(withLoading(createDeveloperRequest()));
      await this.loadUserRegistrationStatus();
    },

    async cancelDeveloperRequest() {
      if (!this.authUserUuid) {
        console.debug(
          "userstore: cancelDeveloperRequest - No user UUID — skipping",
        );
        return;
      }
      await withErrorMessage(withLoading(cancelDeveloperRequest()));
      await this.loadUserRegistrationStatus();
    },

    async clearCookieAndLocalStorageAndState() {
      console.debug("clearCookieAndLocalStorageAndState");
      // clear cookie
      const token = useCookie("token");
      token.value = null;
      // clear local storage and pinia state
      this.clearAuth();
      this.clear();
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
